import React, { Dispatch } from 'react';
import { useIntl } from 'react-intl';

import { Checkmark, Pencil } from '@xsolla/ui-icons';

import { setServerCustomId } from '../../../../api';
import { useNotifications } from '../../../../hooks/useNotifications';
import { validateServerCustomId } from '../../../../utils';
import { ExtraInputButton, FieldInput } from '../../../ui';
import { UserFieldWrapper } from '../style';
import { Action, ActionType, UserState } from '../types';

type Props = {
  state: UserState;
  projectId: string;
  dispatch: Dispatch<Action>;
};

const ChangeServerCustomId = ({ state, projectId, dispatch }: Props): JSX.Element => {
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const {
    user,
    newServerCustomId,
    serverCustomIdEdit,
    serverCustomIdError,
    newServerCustomIdFetching
  } = state;

  const setServerCustomIdHandle = async (server_custom_id: string) => {
    if (!user || !user.id) return;

    try {
      dispatch({ type: ActionType.changeServerCustomIdStart });
      await setServerCustomId(projectId, user.id, {
        server_custom_id
      });
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    } finally {
      dispatch({ type: ActionType.changeServerCustomIdEnd });
    }
  };

  return (
    <UserFieldWrapper>
      <FieldInput
        name="server_custom_id"
        onChange={(e) => {
          dispatch({ type: ActionType.changeServerCustomId, payload: e.target.value });
        }}
        value={newServerCustomId}
        maxLength={255}
        ignoreErrorMeta
        readOnly={!serverCustomIdEdit}
        extraRight={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <ExtraInputButton
            type="button"
            palette="muted"
            disabled={serverCustomIdEdit && newServerCustomId === user.server_custom_id}
            fetching={newServerCustomIdFetching}
            onClick={() => {
              if (serverCustomIdEdit && !validateServerCustomId(newServerCustomId)) {
                dispatch({
                  type: ActionType.changeServerCustomIdError,
                  payload: 'app.validation.value-invalid'
                });
                return;
              }
              if (serverCustomIdEdit) {
                setServerCustomIdHandle(newServerCustomId);
              }
              dispatch({ type: ActionType.serverCustomIdEditToggle });
            }}
            data-test-id="user-change-server-custom-id">
            {serverCustomIdEdit ? <Checkmark /> : <Pencil />}
          </ExtraInputButton>
        }
        label={intl.messages['users.list-block.select-search-by.customId']}
        errorMessage={
          serverCustomIdEdit
            ? serverCustomIdError && intl.formatMessage({ id: serverCustomIdError })
            : ''
        }
      />
    </UserFieldWrapper>
  );
};

export default ChangeServerCustomId;
